export type InvitationLinkParams = {
  token: string;
  type: string;
  id: string;
};

/**
 * Resolves the invitation token/type/id used to continue an invite on web.
 *
 * Stored `link_type` / `task_or_project_id` are only trusted when the token
 * itself also comes from sessionStorage. When the URL carries a token, its
 * (possibly absent) type is authoritative: a generic Contacts invite has no
 * type, and borrowing a stale stored type would misroute it (TA-11035).
 */
export const resolveInvitationLinkParams = ({
  token,
  type,
  id,
}: InvitationLinkParams): InvitationLinkParams => {
  if (token) return { token, type, id };

  return {
    token: window.sessionStorage.getItem('invitation_token') ?? '',
    type: type || (window.sessionStorage.getItem('link_type') ?? ''),
    id: id || (window.sessionStorage.getItem('task_or_project_id') ?? ''),
  };
};

/**
 * Builds the web sign-up URL for an invitation. `type` and `id` are appended
 * only when present so a generic (unscoped) invite keeps its generic
 * registration semantics instead of being classified as a team invite.
 */
export const buildSignupWithEmailUrl = (
  origin: string,
  { token, type, id }: InvitationLinkParams
): string => {
  const baseSignup = `${origin}/register/signup-with-email`;
  if (!token) return baseSignup;

  return `${baseSignup}?invitation_token=${token}${type ? `&type=${type}` : ''}${
    id ? `&id=${id}` : ''
  }`;
};

const GENERIC_INVITATION_KEYS = [
  'invitation_token',
  'link_type',
  'task_or_project_id',
  'task_join_request_id',
] as const;

/**
 * True when sessionStorage holds an invitation token that is not scoped to a
 * team/project/task/chat, i.e. a generic Contacts "Invite Friends" link.
 *
 * A pending chat invite only makes the token a chat invite when it is the
 * same token: app-navigator overwrites `invitation_token` for a new URL but
 * leaves an earlier, abandoned chat invite's keys in place.
 */
export const hasStoredGenericInvitation = (): boolean => {
  const token = window.sessionStorage.getItem('invitation_token');
  return (
    !!token &&
    !window.sessionStorage.getItem('link_type') &&
    !window.sessionStorage.getItem('type') &&
    window.sessionStorage.getItem('pending_chat_invitation_token') !== token
  );
};

/**
 * Drops a stored generic invitation from the shared invite keys (TA-11035).
 *
 * A new sign-up sends the token with `users.json`; an existing account accepts
 * it through acceptUserInvite instead (see `stashGenericInvitationForLogin`).
 * Left in the shared keys it keeps routing a later logout to the register
 * screen and gets attached to an unrelated sign-up in the same tab. Scoped
 * invitations, including a pending chat invite's own keys, are left untouched.
 *
 * @returns whether a generic invitation was cleared
 */
export const clearStoredGenericInvitation = (): boolean => {
  if (!hasStoredGenericInvitation()) return false;
  GENERIC_INVITATION_KEYS.forEach((key) =>
    window.sessionStorage.removeItem(key)
  );
  return true;
};

// Hands a generic token to AppStack's acceptUserInvite call and holds it there
// until the server gives a final answer. Only a login (or AppStack itself)
// sets it, so a fresh sign-up (which already used the token) never triggers
// acceptUserInvite.
const PENDING_GENERIC_INVITE_KEY = 'pending_generic_invite_token';

/** The generic token awaiting acceptUserInvite, if any (not consumed). */
export const getPendingGenericInvitation = (): string | null =>
  window.sessionStorage.getItem(PENDING_GENERIC_INVITE_KEY) || null;

/** Parks a generic token until acceptUserInvite returns a final answer. */
export const setPendingGenericInvitation = (token: string): void => {
  window.sessionStorage.setItem(PENDING_GENERIC_INVITE_KEY, token);
};

/** Drops the parked token once acceptUserInvite has answered. */
export const clearPendingGenericInvitation = (): void => {
  window.sessionStorage.removeItem(PENDING_GENERIC_INVITE_KEY);
};

/**
 * Called when an existing account logs in: moves a stored generic invitation
 * out of the shared invite keys and parks it for AppStack, which accepts it
 * with acceptUserInvite once the user is signed in.
 */
export const stashGenericInvitationForLogin = (): void => {
  const token = window.sessionStorage.getItem('invitation_token');
  if (!token || !clearStoredGenericInvitation()) return;
  setPendingGenericInvitation(token);
};
